import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { setPostLoginRedirect } from 'common/auth/postLoginRedirect';
import type { SsoSessionKeys } from 'common/auth/sso/sso.types';
import type { ActiveStudySite } from 'common/auth/types';
import { takeOverFlowInThisTab } from 'common/utils/browserTabFlowOwner';

import { DeviceHandoffOrchestratorActions } from '../effects/orchestrator';
import { DeviceHandoffActions } from '../state/actions';
import type { ActivityGate, RespondentTarget } from '../state/types';

export function useDeviceHandoffActions() {
    const dispatch = useDispatch();

    const initiate = useCallback(
        (
            target: RespondentTarget,
            gate: ActivityGate,
            caregiverNames: string[],
            activeStudySite: ActiveStudySite,
            activityLocation?: string
        ) => {
            takeOverFlowInThisTab('deviceHandoff');
            dispatch(DeviceHandoffActions.initiate(target, gate, caregiverNames, activeStudySite, activityLocation));
        },
        [dispatch]
    );

    const submitCredentials = useCallback(
        (username: string | null, password: string | null, ssoTokens?: SsoSessionKeys) =>
            dispatch(DeviceHandoffOrchestratorActions.submitCredentials(username, password, ssoTokens)),
        [dispatch]
    );

    const retry = useCallback(() => dispatch(DeviceHandoffActions.retry()), [dispatch]);

    const endRespondentTurn = useCallback(
        (returnPath?: string) => {
            if (returnPath) {
                setPostLoginRedirect(returnPath);
            }
            dispatch(DeviceHandoffActions.respondentTurnEnded());
        },
        [dispatch]
    );

    return {
        initiate,
        submitCredentials,
        retry,
        endRespondentTurn,
    };
}
